import { useState } from "react";
import Image from "next/image";
import { MotionDiv } from "@/components/ui/MotionDiv";
import { eventsData } from "@/data/events";
import remiseceinture from "@/assets/images/gallery/remise-ceinture.webp";
import stage from "@/assets/images/gallery/stage.webp";
import competition from "@/assets/images/gallery/competition.webp";
import { GalleryGrid } from "./GalleryGrid";
import { isPast } from "date-fns";

type GalleryCategory = "competitions" | "stagesAndDemonstrations" | "Autres";

const categoryImages = {
  competitions: competition,
  stagesAndDemonstrations: stage,
  Autres: remiseceinture,
};

const categoryLabels: Record<GalleryCategory, string[]> = {
  competitions: ["COMPÉTITIONS"],
  stagesAndDemonstrations: ["STAGES SPORTIFS,", "DÉMONSTRATIONS"],
  Autres: ["AUTRES", "ÉVÉNEMENTS"],
};

export const GallerySelection = () => {
  const getMostRecentEvent = () => {
    const pastEvents = eventsData
      .filter((event) => isPast(new Date(event.date)))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return pastEvents[0];
  };

  const mostRecentEvent = getMostRecentEvent();
  const [selectedCategory, setSelectedCategory] = useState<GalleryCategory>(
    mostRecentEvent?.category || "competitions",
  );
  const [selectedEvent, setSelectedEvent] = useState<string>(
    mostRecentEvent?.date || "",
  );
  const [selectedYear, setSelectedYear] = useState<string>(
    new Date(mostRecentEvent?.date || Date.now()).getFullYear().toString(),
  );

  // Get unique years from events
  const getYearsForCategory = (category: GalleryCategory) => {
    const years = eventsData
      .filter(
        (event) => event.category === category && isPast(new Date(event.date)),
      )
      .map((event) => new Date(event.date).getFullYear())
      .filter((year, index, self) => self.indexOf(year) === index)
      .sort((a, b) => b - a);

    return years;
  };

  // Filter events by category and year
  const getEventsByCategory = (category: GalleryCategory, year: string) => {
    return eventsData
      .filter(
        (event) =>
          event.category === category &&
          isPast(new Date(event.date)) &&
          new Date(event.date).getFullYear() === parseInt(year),
      )
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  const selectedEventData = eventsData.find(
    (event) => event.date === selectedEvent,
  );

  const handleCategoryChange = (category: GalleryCategory) => {
    setSelectedCategory(category);
    const years = getYearsForCategory(category);
    if (years.length > 0) {
      const newYear = years[0].toString();
      setSelectedYear(newYear);
      const events = getEventsByCategory(category, newYear);
      if (events.length > 0) {
        setSelectedEvent(events[0].date);
      }
    }
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    const events = getEventsByCategory(selectedCategory, year);
    if (events.length > 0) {
      setSelectedEvent(events[0].date);
    }
  };

  return (
    <>
      <section className="flex flex-col items-center justify-center gap-20 px-4 py-20 ph:px-24 md:px-48 lg:px-72 xl:px-96">
        <div className="grid w-full grid-cols-1 gap-8 md:grid-cols-3">
          {(Object.keys(categoryImages) as GalleryCategory[]).map(
            (category) => (
              <div
                key={category}
                className="group flex cursor-pointer flex-col"
                onClick={() => handleCategoryChange(category)}
              >
                <div className="relative mb-4 overflow-hidden">
                  <Image
                    loading="lazy"
                    src={categoryImages[category]}
                    alt={categoryLabels[category][0]}
                    className={`aspect-[4/3] w-full object-cover transition-all duration-300 ${
                      selectedCategory !== category
                        ? "brightness-50 grayscale"
                        : "brightness-100"
                    }`}
                  />
                </div>
                <div className="flex flex-col items-start">
                  {categoryLabels[category].map((line, index) => (
                    <span
                      key={index}
                      className={`text-lg font-medium transition-colors duration-300 ${
                        selectedCategory !== category
                          ? "text-white/60"
                          : "text-white"
                      }`}
                    >
                      {line}
                    </span>
                  ))}
                </div>
              </div>
            ),
          )}
        </div>

        {/* Year Selection */}
        <div className="w-full">
          <div className="relative inline-block">
            <select
              title="Sélectionner une année"
              value={selectedYear}
              onChange={(e) => handleYearChange(e.target.value)}
              className="h-12 w-32 appearance-none rounded-md border border-white/20 bg-transparent px-6 text-lg text-white outline-none transition-colors hover:border-white/40 focus:border-white/60"
            >
              {getYearsForCategory(selectedCategory).map((year) => (
                <option key={year} value={year} className="bg-black">
                  {year}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
              <svg
                className="h-4 w-4 fill-current text-white/60"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-8">
            {getEventsByCategory(selectedCategory, selectedYear).map(
              (event, index) => (
                <div key={index} className="flex justify-start">
                  <button
                    onClick={() => setSelectedEvent(event.date)}
                    className="relative inline-block pb-1 text-left"
                  >
                    <span
                      className={`${
                        selectedEvent === event.date
                          ? "font-medium text-white"
                          : "text-white/70 hover:text-white"
                      } relative transition-colors`}
                    >
                      {event.labelSelection}
                      {selectedEvent === event.date && (
                        <MotionDiv
                          className="absolute -bottom-[3px] left-0 h-0.5 w-full bg-gradient-to-r from-[#F9F383] via-[#F14553] to-[#19477E]"
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          transition={{
                            duration: 0.4,
                            ease: "easeOut",
                          }}
                        />
                      )}
                    </span>
                  </button>
                </div>
              ),
            )}
          </div>
        </div>
      </section>

      {selectedEventData &&
        selectedEventData.images &&
        selectedEventData.images.length > 0 && (
          <GalleryGrid
            title={selectedEventData.desktopTitle}
            images={selectedEventData.images}
            mobileTitle={selectedEventData.mobileTitle}
          />
        )}
    </>
  );
};
